import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';
import config from 'devextreme/core/config';
import CustomStore from 'devextreme/data/custom_store';
import {confirm} from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import * as Moment from 'moment-timezone';
import {BehaviorSubject, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {Role, User} from 'src/app/shared/models/fusion-auth';
import {FusionAuthManagementService, FusionAuthService} from '../../../shared/services/fusion-auth';
import {ScreenService} from '../../../shared/services/general';
import DataSource from 'devextreme/data/data_source';
import {SelectionChangedEvent, ToolbarPreparingEvent} from "devextreme/ui/data_grid";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
})
export class UserComponent implements OnInit, OnDestroy {
  @ViewChild(DxDataGridComponent, {static: true}) public grid?: DxDataGridComponent;
  public store: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null as unknown as DataSource);
  public roles: BehaviorSubject<Role[]> = new BehaviorSubject([] as Role[]);
  public formVisible = false;
  public selectedUser?: User;
  public users?: CustomStore;
  public isPhoneOrTablet: boolean = false;
  public isPhone: boolean = false;
  public selectedRowData: any[] = [];
  public onDestroy$: Subject<void> = new Subject<void>();

  constructor(private fusionAuthManagement: FusionAuthManagementService, public fusionAuth: FusionAuthService,
              private screen: ScreenService) {
  }

  public ngOnInit() {
    const role = this.fusionAuth.Profile.registrations[0].roles[0];
    this.store.next(new DataSource({
      store: role === 'Client Admin' ? this.fusionAuthManagement.getUsers(this.fusionAuth.Profile.data.clients, this.store)
        : this.fusionAuthManagement.getUsers([], this.store)
    }));

    this.fusionAuthManagement.getApplicationRoles()
      .pipe(take(1))
      .subscribe(r => {
        if (role === 'Client Admin') {
          r = r.filter(x => x.name !== 'Superuser');
          r = r.filter(x => x.name !== 'Physician');
          r = r.filter(x => x.name !== 'Patient');
        }
        this.roles.next(r);
      });

    this.responsiveValues();
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.responsiveValues();
    });
  }

  private responsiveValues() {
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.isPhone = this.screen.sizes['screen-x-small'];

    const offset = this.isPhone ? '-15 75' : '-25 75';
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        position: {
          of: 'body',
          my: 'right top',
          at: 'right top',
          offset,
        },
      },
    });
    repaintFloatingActionButton();
  }

  public onToolbarPreparing(e: ToolbarPreparingEvent) {
    e?.toolbarOptions?.items?.unshift({
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: 'plus',
        text: 'Add',
        onClick: () => (this.formVisible = true),
      },
    });
  }

  public sendToEdit(user: User) {
    this.selectedUser = user;
    this.formVisible = true;
  }

  public submitted() {
    this.formVisible = false;
    this.grid?.instance.getDataSource()
      .reload()
      .then(() => {
        this.grid?.instance.clearSelection();
      });
  }

  public async remove(key: string) {
    if (await confirm('Do you want to delete the record?', 'Delete')) {
      this.fusionAuthManagement.deleteUser(key)
        .pipe(take(1))
        .subscribe(() => {
          this.grid?.instance.getDataSource().reload().then(() => {
            this.grid?.instance.refresh().then(() => {
              this.grid?.instance.clearSelection();
            });
          });
        });
    }
  }

  public parseDate(date: any): string {
    if (date === null) {
      return 'Never';
    } else {
      const tz = Moment.tz.guess();
      return Moment.utc(date).tz(tz).format('LLLL');
    }
  }

  public resendEmail(email: string) {
    this.fusionAuth.forgotPassword({
      loginId: email,
      sendForgotPasswordEmail: true,
    })
      .pipe(take(1))
      .subscribe(() => {
        notify('A notification has been sent to email ' + email, 'info', 3000);
      }, (error) => {
        notify(error.message, 'error', 3000);
      });
  }

  public itemsSelected(e: SelectionChangedEvent) {
    this.selectedRowData = e.selectedRowsData;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
